import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";

const MS_PER_SEC = 1000;
const MS_PER_MIN = 60 * MS_PER_SEC;
const MS_PER_HOUR = 60 * MS_PER_MIN;

function formatHMS(ms) {
  const safe = Math.max(0, Math.floor(ms));
  const h = Math.floor(safe / MS_PER_HOUR);
  const m = Math.floor((safe % MS_PER_HOUR) / MS_PER_MIN);
  const s = Math.floor((safe % MS_PER_MIN) / MS_PER_SEC);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts === "number") return ts;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts.toMillis === "function") return ts.toMillis();
  return 0;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfWeek(d) {
  const x = startOfDay(d);
  const day = x.getDay(); // 0=Sun
  const diff = (day + 6) % 7; // make Monday=0
  x.setDate(x.getDate() - diff);
  return x;
}

function startOfMonth(d) {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
}

function toDateInputValue(d) {
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
}

function dateFromInputValue(value) {
  // Value format: YYYY-MM-DD
  if (!value) return null;
  const [y, m, d] = value.split("-").map((p) => Number(p));
  if (!y || !m || !d) return null;
  const x = new Date(y, m - 1, d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function computeLiveTotals(session, nowMs) {
  if (!session) {
    return {
      workMs: 0,
      breakMs: 0,
      balanceMs: 0,
    };
  }

  const status = session.status;
  const lastChangeMs = toMillis(session.lastStateChangeAt);

  const accumulatedWorkMs = Number(session.accumulatedWorkMs || 0);
  const accumulatedBreakMs = Number(session.accumulatedBreakMs || 0);

  const runningDelta = Math.max(0, nowMs - lastChangeMs);

  const liveWorkMs =
    accumulatedWorkMs + (status === "active" ? runningDelta : 0);
  const liveBreakMs =
    accumulatedBreakMs + (status === "break" ? runningDelta : 0);

  return {
    workMs: liveWorkMs,
    breakMs: liveBreakMs,
  };
}

export default function WorkTracker({ theme }) {
  const { currentUser, isEmployee } = useAuth();

  const [activeSession, setActiveSession] = useState(null);
  const [laps, setLaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowMs, setNowMs] = useState(Date.now());
  const [recentSessions, setRecentSessions] = useState([]);
  const [actionError, setActionError] = useState(null);

  const [selectedDay, setSelectedDay] = useState(() =>
    toDateInputValue(new Date())
  );
  const [selectedDayVideos, setSelectedDayVideos] = useState(0);
  const [selectedDayLoading, setSelectedDayLoading] = useState(false);

  const lapsUnsubRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Work Tracker - ASites Planner";
  }, []);

  // Tick for display only; authoritative state comes from Firestore fields.
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const sessionsCol = useMemo(() => {
    if (!currentUser) return null;
    return collection(doc(db, "users", currentUser.uid), "workSessions");
  }, [currentUser]);

  const loadRecentSessions = useCallback(async () => {
    if (!sessionsCol) return;

    const now = new Date();
    const monthStart = startOfMonth(now);

    const q = query(
      sessionsCol,
      where("startAt", ">=", Timestamp.fromDate(monthStart)),
      orderBy("startAt", "desc")
    );

    const snap = await getDocs(q);
    setRecentSessions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, [sessionsCol]);

  const loadSelectedDayVideos = useCallback(
    async (dayValue) => {
      if (!sessionsCol) return;
      const day = dateFromInputValue(dayValue);
      if (!day) return;

      const nextDay = new Date(day);
      nextDay.setDate(nextDay.getDate() + 1);

      setSelectedDayLoading(true);
      try {
        const q = query(
          sessionsCol,
          where("startAt", ">=", Timestamp.fromDate(day)),
          where("startAt", "<", Timestamp.fromDate(nextDay)),
          orderBy("startAt", "desc")
        );

        const snap = await getDocs(q);

        let total = 0;
        snap.docs.forEach((d) => {
          const s = { id: d.id, ...d.data() };

          // If this is the active session, prefer the live lapCount.
          if (activeSession && s.id === activeSession.id) {
            total += Number(activeSession.lapCount || 0);
          } else {
            total += Number(s.lapCount || 0);
          }
        });

        // If the active session started on this day but wasn't returned (shouldn't happen,
        // but keep it safe), include it.
        if (activeSession) {
          const activeStart = toMillis(activeSession.startAt);
          if (activeStart >= day.getTime() && activeStart < nextDay.getTime()) {
            const alreadyCounted = snap.docs.some(
              (d) => d.id === activeSession.id
            );
            if (!alreadyCounted) {
              total += Number(activeSession.lapCount || 0);
            }
          }
        }

        setSelectedDayVideos(total);
      } catch (e) {
        setActionError(e?.message || "Failed to load selected day stats.");
      } finally {
        setSelectedDayLoading(false);
      }
    },
    [sessionsCol, activeSession]
  );

  // Subscribe to active session
  useEffect(() => {
    if (!sessionsCol || !currentUser) {
      setActiveSession(null);
      setLaps([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Important: avoid orderBy here to prevent composite-index requirements.
    const q = query(sessionsCol, where("isActive", "==", true), limit(1));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docSnap = snap.docs[0];
        if (!docSnap) {
          setActiveSession(null);
          setLaps([]);
          if (lapsUnsubRef.current) {
            lapsUnsubRef.current();
            lapsUnsubRef.current = null;
          }
          setLoading(false);
          return;
        }

        const session = { id: docSnap.id, ...docSnap.data() };
        setActiveSession(session);

        // Subscribe to laps for this session
        if (lapsUnsubRef.current) {
          lapsUnsubRef.current();
          lapsUnsubRef.current = null;
        }

        const lapsCol = collection(docSnap.ref, "laps");
        const lapsQ = query(lapsCol, orderBy("index", "desc"));
        lapsUnsubRef.current = onSnapshot(lapsQ, (lapsSnap) => {
          setLaps(lapsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });

        setLoading(false);
      },
      (err) => {
        setActionError(
          err?.message ||
            "Failed to load your active session (check Firestore permissions/indexes)."
        );
        setLoading(false);
      }
    );

    return () => {
      unsub();
      if (lapsUnsubRef.current) {
        lapsUnsubRef.current();
        lapsUnsubRef.current = null;
      }
    };
  }, [sessionsCol, currentUser]);

  // Refresh analytics when sessions change or active session changes.
  useEffect(() => {
    if (!sessionsCol) return;
    loadRecentSessions();
  }, [sessionsCol, activeSession?.id, loadRecentSessions]);

  // Load selected-day video totals.
  useEffect(() => {
    if (!sessionsCol) return;
    loadSelectedDayVideos(selectedDay);
  }, [
    sessionsCol,
    selectedDay,
    activeSession?.lapCount,
    loadSelectedDayVideos,
  ]);

  const live = useMemo(() => {
    return computeLiveTotals(activeSession, nowMs);
  }, [activeSession, nowMs]);

  const todayStats = useMemo(() => {
    const now = new Date(nowMs);
    const dayStart = startOfDay(now).getTime();
    const threeDaysStart = startOfDay(
      new Date(nowMs - 2 * 24 * 60 * 60 * 1000)
    );
    const weekStart = startOfWeek(now);
    const monthStart = startOfMonth(now);

    const sessions = recentSessions || [];

    const inRange = (session, startDate) => {
      const s = toMillis(session.startAt);
      return s >= startDate.getTime();
    };

    const sum = (filterStartDate) => {
      let videos = 0;
      let workMs = 0;
      let breakMs = 0;

      sessions.forEach((s) => {
        if (!inRange(s, filterStartDate)) return;
        videos += Number(s.lapCount || 0);
        workMs += Number(s.totalWorkMs ?? s.accumulatedWorkMs ?? 0);
        breakMs += Number(s.totalBreakMs ?? s.accumulatedBreakMs ?? 0);
      });

      return { videos, workMs, breakMs };
    };

    const month = sum(monthStart);
    const week = sum(weekStart);
    const last3Days = sum(threeDaysStart);
    const today = sum(new Date(dayStart));

    // Include active session live counters into today/week/month if applicable
    if (activeSession) {
      const activeStart = toMillis(activeSession.startAt);
      const addTo = (bucketStart) => activeStart >= bucketStart.getTime();
      const activeVideos = Number(activeSession.lapCount || 0);

      if (addTo(new Date(dayStart))) {
        today.videos = activeVideos;
        today.workMs = live.workMs;
        today.breakMs = live.breakMs;
      }

      if (addTo(threeDaysStart)) {
        // For simplicity, include active session by recomputing within range
        // (lapCount/workMs already represent full session so far)
        last3Days.videos =
          last3Days.videos - Number(activeSession.lapCount || 0) + activeVideos;
      }

      if (addTo(weekStart)) {
        week.videos =
          week.videos - Number(activeSession.lapCount || 0) + activeVideos;
      }

      if (addTo(monthStart)) {
        month.videos =
          month.videos - Number(activeSession.lapCount || 0) + activeVideos;
      }
    }

    const avgTimePerVideoMs = today.videos
      ? Math.round(today.workMs / today.videos)
      : 0;

    const videosPerHour = today.workMs
      ? Number((today.videos / (today.workMs / MS_PER_HOUR)).toFixed(2))
      : 0;

    return {
      today,
      last3Days,
      week,
      month,
      videosPerHour,
      avgTimePerVideoMs,
    };
  }, [recentSessions, activeSession, live, nowMs]);

  const writeEvent = useCallback(async (sessionRef, type, extra = {}) => {
    try {
      await addDoc(collection(sessionRef, "events"), {
        type,
        at: Timestamp.fromMillis(Date.now()),
        createdAt: serverTimestamp(),
        ...extra,
      });
    } catch {
      // non-fatal
    }
  }, []);

  const startWork = useCallback(async () => {
    if (!sessionsCol || !currentUser) return;
    if (activeSession?.isActive) return;

    setActionError(null);

    const now = Timestamp.fromMillis(Date.now());

    const sessionDoc = {
      isActive: true,
      status: "active",
      startAt: now,
      endAt: null,
      lastStateChangeAt: now,
      accumulatedWorkMs: 0,
      accumulatedBreakMs: 0,
      lapCount: 0,
      lastLapAt: now,
      lastLapWorkElapsedMs: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const ref = await addDoc(sessionsCol, sessionDoc);

      // Optional: store active session id on user profile (best-effort)
      try {
        await setDoc(
          doc(db, "users", currentUser.uid),
          { activeWorkSessionId: ref.id },
          { merge: true }
        );
      } catch {
        // non-fatal
      }

      await writeEvent(ref, "start");
    } catch (e) {
      setActionError(
        e?.message ||
          "Failed to start session. If you’re admin, initialize the allowlist in Admin Dashboard."
      );
    }
  }, [sessionsCol, currentUser, activeSession, writeEvent]);

  const takeBreak = useCallback(async () => {
    if (!currentUser || !activeSession) return;
    if (activeSession.status !== "active") return;

    setActionError(null);

    const now = Date.now();
    const lastChangeMs = toMillis(activeSession.lastStateChangeAt);
    const workDelta = Math.max(0, now - lastChangeMs);

    const sessionRef = doc(
      db,
      "users",
      currentUser.uid,
      "workSessions",
      activeSession.id
    );

    try {
      await updateDoc(sessionRef, {
        status: "break",
        lastStateChangeAt: Timestamp.fromMillis(now),
        accumulatedWorkMs:
          Number(activeSession.accumulatedWorkMs || 0) + workDelta,
        updatedAt: serverTimestamp(),
      });

      await writeEvent(sessionRef, "break_start", { workDeltaMs: workDelta });
    } catch (e) {
      setActionError(e?.message || "Failed to start break.");
    }
  }, [currentUser, activeSession, writeEvent]);

  const resumeWork = useCallback(async () => {
    if (!currentUser || !activeSession) return;
    if (activeSession.status !== "break") return;

    setActionError(null);

    const now = Date.now();
    const lastChangeMs = toMillis(activeSession.lastStateChangeAt);
    const breakDelta = Math.max(0, now - lastChangeMs);

    const sessionRef = doc(
      db,
      "users",
      currentUser.uid,
      "workSessions",
      activeSession.id
    );

    try {
      await updateDoc(sessionRef, {
        status: "active",
        lastStateChangeAt: Timestamp.fromMillis(now),
        accumulatedBreakMs:
          Number(activeSession.accumulatedBreakMs || 0) + breakDelta,
        updatedAt: serverTimestamp(),
      });

      await writeEvent(sessionRef, "break_end", { breakDeltaMs: breakDelta });
    } catch (e) {
      setActionError(e?.message || "Failed to resume work.");
    }
  }, [currentUser, activeSession, writeEvent]);

  const lapVideo = useCallback(async () => {
    if (!currentUser || !activeSession) return;
    if (activeSession.status !== "active") return;

    setActionError(null);

    const now = Date.now();
    const sessionRef = doc(
      db,
      "users",
      currentUser.uid,
      "workSessions",
      activeSession.id
    );

    const liveNow = computeLiveTotals(activeSession, now);
    const workElapsedMs = liveNow.workMs;

    const prevLapWorkElapsedMs = Number(
      activeSession.lastLapWorkElapsedMs || 0
    );
    const durationMs = Math.max(0, workElapsedMs - prevLapWorkElapsedMs);

    const nextIndex = Number(activeSession.lapCount || 0) + 1;

    try {
      await addDoc(collection(sessionRef, "laps"), {
        index: nextIndex,
        at: Timestamp.fromMillis(now),
        durationMs,
        workElapsedMs,
        createdAt: serverTimestamp(),
      });

      await updateDoc(sessionRef, {
        lapCount: nextIndex,
        lastLapAt: Timestamp.fromMillis(now),
        lastLapWorkElapsedMs: workElapsedMs,
        updatedAt: serverTimestamp(),
      });

      await writeEvent(sessionRef, "lap", { index: nextIndex, durationMs });
    } catch (e) {
      setActionError(e?.message || "Failed to record lap.");
    }
  }, [currentUser, activeSession, writeEvent]);

  const endSession = useCallback(async () => {
    if (!currentUser || !activeSession) return;

    setActionError(null);

    const now = Date.now();
    const lastChangeMs = toMillis(activeSession.lastStateChangeAt);
    const delta = Math.max(0, now - lastChangeMs);

    const wasActive = activeSession.status === "active";
    const wasBreak = activeSession.status === "break";

    const totalWorkMs =
      Number(activeSession.accumulatedWorkMs || 0) + (wasActive ? delta : 0);
    const totalBreakMs =
      Number(activeSession.accumulatedBreakMs || 0) + (wasBreak ? delta : 0);

    const sessionRef = doc(
      db,
      "users",
      currentUser.uid,
      "workSessions",
      activeSession.id
    );

    try {
      await updateDoc(sessionRef, {
        isActive: false,
        status: "ended",
        endAt: Timestamp.fromMillis(now),
        lastStateChangeAt: Timestamp.fromMillis(now),
        accumulatedWorkMs: totalWorkMs,
        accumulatedBreakMs: totalBreakMs,
        totalWorkMs,
        totalBreakMs,
        updatedAt: serverTimestamp(),
      });

      // best-effort: clear active session pointer
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const snap = await getDoc(userRef);
        if (
          snap.exists() &&
          snap.data()?.activeWorkSessionId === activeSession.id
        ) {
          await updateDoc(userRef, { activeWorkSessionId: null });
        }
      } catch {
        // non-fatal
      }

      await writeEvent(sessionRef, "stop", { deltaMs: delta });
    } catch (e) {
      setActionError(e?.message || "Failed to end session.");
    }
  }, [currentUser, activeSession, writeEvent]);

  if (!currentUser) {
    return null;
  }

  if (!isEmployee) {
    return null;
  }

  const status = activeSession?.status || "idle";
  const videosDone = Number(activeSession?.lapCount || 0);

  return (
    <Page>
      <Navbar theme={theme} />
      <Header>
        <Title>Work Tracker</Title>
        <Subtitle>
          Track work sessions, breaks, and completed videos (laps).
        </Subtitle>
      </Header>

      {actionError && (
        <Banner>
          {actionError} <br />
          If you’re the admin, go to Admin Dashboard → Initialize/Add emails.
        </Banner>
      )}

      <Grid>
        <Card>
          <CardTitle>Stopwatch</CardTitle>

          <TimerRow>
            <TimerBlock>
              <TimerLabel>Work time</TimerLabel>
              <TimerValue>{formatHMS(live.workMs)}</TimerValue>
            </TimerBlock>
            <TimerBlock>
              <TimerLabel>Break time</TimerLabel>
              <TimerValue muted>{formatHMS(live.breakMs)}</TimerValue>
            </TimerBlock>
            <TimerBlock>
              <TimerLabel>Videos</TimerLabel>
              <TimerValue>{videosDone}</TimerValue>
            </TimerBlock>
          </TimerRow>

          <ButtonRow>
            <PrimaryButton onClick={startWork} disabled={status !== "idle"}>
              Start Work
            </PrimaryButton>

            {status === "active" ? (
              <SecondaryButton onClick={takeBreak}>Take Break</SecondaryButton>
            ) : (
              <SecondaryButton
                onClick={resumeWork}
                disabled={status !== "break"}
              >
                Resume
              </SecondaryButton>
            )}

            <DangerButton
              onClick={endSession}
              disabled={status === "idle" || status === "ended"}
            >
              Stop / End Session
            </DangerButton>
          </ButtonRow>

          <ButtonRow>
            <GhostButton onClick={lapVideo} disabled={status !== "active"}>
              Lap (Video Completed)
            </GhostButton>
          </ButtonRow>

          {loading && <Hint>Loading session…</Hint>}
          {!loading && status === "idle" && (
            <Hint>No active session. Click “Start Work”.</Hint>
          )}
          {status === "break" && <Hint>On break — work timer paused.</Hint>}
        </Card>

        <Card>
          <CardTitle>Today’s Stats</CardTitle>

          <DayPickerRow>
            <DayPickerLabel>Pick a day</DayPickerLabel>
            <DayPickerInput
              type="date"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
            />
          </DayPickerRow>

          <SelectedDayStat>
            <div>
              <SelectedDayLabel>Videos on selected day</SelectedDayLabel>
              <SelectedDayValue>
                {selectedDayLoading ? "Loading…" : selectedDayVideos}
              </SelectedDayValue>
            </div>
          </SelectedDayStat>

          <StatsGrid>
            <StatBox>
              <StatLabel>Videos per hour</StatLabel>
              <StatValue>{todayStats.videosPerHour || 0}</StatValue>
            </StatBox>
            <StatBox>
              <StatLabel>Total videos today</StatLabel>
              <StatValue>{todayStats.today.videos}</StatValue>
            </StatBox>
            <StatBox>
              <StatLabel>Last 3 days</StatLabel>
              <StatValue>{todayStats.last3Days.videos}</StatValue>
            </StatBox>
            <StatBox>
              <StatLabel>This week</StatLabel>
              <StatValue>{todayStats.week.videos}</StatValue>
            </StatBox>
            <StatBox>
              <StatLabel>This month</StatLabel>
              <StatValue>{todayStats.month.videos}</StatValue>
            </StatBox>
            <StatBox>
              <StatLabel>Avg time / video</StatLabel>
              <StatValue>{formatHMS(todayStats.avgTimePerVideoMs)}</StatValue>
            </StatBox>
          </StatsGrid>
        </Card>

        <CardWide>
          <CardTitle>Laps (Videos Completed)</CardTitle>
          {laps.length === 0 ? (
            <Hint>No laps yet.</Hint>
          ) : (
            <LapList>
              {laps.map((l) => (
                <LapRow key={l.id}>
                  <div>
                    <LapIndex>#{l.index}</LapIndex>
                    <LapMeta>
                      {l.at?.toDate ? l.at.toDate().toLocaleString() : ""}
                    </LapMeta>
                  </div>
                  <LapDuration>
                    {formatHMS(Number(l.durationMs || 0))}
                  </LapDuration>
                </LapRow>
              ))}
            </LapList>
          )}
        </CardWide>
      </Grid>
    </Page>
  );
}

const Page = styled.div`
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.header`
  margin-left: 5%;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5em;
  padding: 0 5%;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 2em;
  border-radius: 14px;
  color: var(--fontPrimaryColor);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const CardWide = styled(Card)`
  grid-column: 1 / -1;
`;

const CardTitle = styled.h3`
  margin: 0 0 12px 0;
  font-weight: 600;
`;

const TimerRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const TimerBlock = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 12px;
  padding: 14px 16px;
`;

const TimerLabel = styled.div`
  font-size: 13px;
  color: var(--fontSecondaryColor);
  margin-bottom: 6px;
`;

const TimerValue = styled.div`
  font-size: 26px;
  font-weight: 600;
  color: ${(p) => (p.muted ? "var(--fontSecondaryColor)" : "inherit")};
`;

const DayPickerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 10px 0 12px 0;
  flex-wrap: wrap;
`;

const DayPickerLabel = styled.div`
  font-size: 13px;
  color: var(--fontSecondaryColor);
`;

const DayPickerInput = styled.input`
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;
`;

const SelectedDayStat = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 14px;
`;

const SelectedDayLabel = styled.div`
  font-size: 13px;
  color: var(--fontSecondaryColor);
  margin-bottom: 6px;
`;

const SelectedDayValue = styled.div`
  font-size: 26px;
  font-weight: 600;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const BaseButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: var(--primaryColor);
  color: white;
`;

const SecondaryButton = styled(BaseButton)`
  background: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
`;

const GhostButton = styled(BaseButton)`
  background: #4ccf4c9b;
  color: var(--fontPrimaryColor);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const DangerButton = styled(BaseButton)`
  background: rgba(239, 68, 68, 0.2);
  color: rgba(239, 68, 68, 0.95);
`;

const Hint = styled.div`
  margin-top: 10px;
  color: var(--fontSecondaryColor);
  font-size: 14px;
`;

const Banner = styled.div`
  margin: 0 5%;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: rgba(239, 68, 68, 0.95);
  font-weight: 600;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 12px;
  padding: 14px 16px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: var(--fontSecondaryColor);
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 600;
`;

const LapList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 420px;
  overflow: auto;
`;

const LapRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 12px 14px;
`;

const LapIndex = styled.div`
  font-weight: 700;
`;

const LapMeta = styled.div`
  font-size: 13px;
  color: var(--fontSecondaryColor);
  margin-top: 2px;
`;

const LapDuration = styled.div`
  font-weight: 600;
  color: var(--fontPrimaryColor);
`;
